// TipoRutinaDetail.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Select, MenuItem, FormControl,
  InputLabel, FormControlLabel, Checkbox, Switch,IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useParams } from 'react-router-dom';
import api from './api';
import { StyledMainContainer } from './StyleComponents';
import Header from './Header';

const createEmptyQuestion = () => ({
  pregunta: '',
  orden: '',
  tipo_respuesta: '',
  tipo_respuesta_label: '',
  opciones: [],
  opcion_temp: '',
  aplica_semaforo: false,
  maximo: '',
  minimo: '',
  respuesta_correcta_lista: '',
  lista: []
});

function TipoRutinaDetail({ onSave }) {
  const { id } = useParams();
  const [tiposRespuestas, setTiposRespuestas] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '', fabricante_id: '', periodicidad: '', ejecutor: '', is_active: true,
    preguntas: []
  });
  const [originalFormData, setOriginalFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const roles = ['admin', 'operator', 'coordinador', 'supervisor', 'tecnico'];
  const lista_periodicidad = ['Diaria', 'Semanal', 'Mensual', 'Semestral'];
  const isSemaforoCompatible = (label) => label === 'numero' || label === 'lista';


  useEffect(() => {
    const fetchData = async () => {
      const [resRutina, resTipos] = await Promise.all([
        api.get(`/tipos_rutinas/${id}`),
        api.get('/tipos_respuestas/?skip=0&limit=1000')
      ]);

      const tipos = resTipos.data;
      setTiposRespuestas(tipos);

      const preguntasAdaptadas = (resRutina.data.preguntas || []).map(p => {
        return {
          ...p,
          tipo_respuesta: p.id_tipo_respuesta,
          tipo_respuesta_label: tipos.find(t => t.id === p.id_tipo_respuesta)?.tipo || '',

          lista: p.lista || [],
          opcion_temp: '',
          aplica_semaforo: !!p.semaforo,
          maximo: p.semaforo?.rango_max || '',
          minimo: p.semaforo?.rango_min || '',
          respuesta_correcta_lista: p.semaforo?.valor_objetivo || ''
        };
      });

      setFormData({ ...resRutina.data, preguntas: preguntasAdaptadas });
      setOriginalFormData({ ...resRutina.data, preguntas: preguntasAdaptadas });
    };
    fetchData();
  }, [id]);


    // Borra en cascada: opciones de lista -> semáforo -> pregunta
  const deletePreguntaCascade = async (preguntaId) => {
    // 1. Traer la pregunta completa para asegurar ids de lista y semáforo
    const res = await api.get(`/preguntas_rutinas/${preguntaId}`);
    const detalle = res.data || {};
    const opciones = detalle.lista || [];
    const semaforo = detalle.semaforo;

    // 2. Borrar opciones de lista (si existen)
    //for (const op of opciones) {
     // if (op?.id) {
      //  await api.delete(`/listas_respuestas/${op.id}`);
      //}
    //}

    // 3. Borrar semáforo (si existe)
    //if (semaforo?.id) {
     // await api.delete(`/semaforos/${semaforo.id}`);
    //}

    // 4. Borrar la pregunta
    await api.delete(`/preguntas_rutinas/${preguntaId}`);
  };


  const handleRoutineDetailChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuestionChange = (index, fieldOrEvent, maybeValue = null) => {
    let name, value, checked, type;

    // Detectar si se llamó con evento o directamente
    if (typeof fieldOrEvent === 'object' && fieldOrEvent?.target) {
      ({ name, value, checked, type } = fieldOrEvent.target);
    } else {
      name = fieldOrEvent;
      value = maybeValue;
      type = typeof value === 'boolean' ? 'checkbox' : 'text';
      checked = value;
    }

    setFormData(prev => {
      const questions = [...prev.preguntas];
      questions[index] = {
        ...questions[index],
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'tipo_respuesta') {
        const selectedTipo = tiposRespuestas.find(tr => tr.id === value);
        questions[index].tipo_respuesta_label = selectedTipo?.tipo || '';
        
        // Si el nuevo tipo NO es compatible con semáforo -> apagar y limpiar
        if (!isSemaforoCompatible(questions[index].tipo_respuesta_label)) {
          questions[index].semaforo = null;
          questions[index].maximo = '';
          questions[index].minimo = '';
          questions[index].respuesta_correcta_lista = '';
        }


        if (questions[index].semaforo) {
        if (questions[index].tipo_respuesta_label === 'numero') {
          // Pasaste a número: limpia objetivo
          questions[index].semaforo.valor_objetivo = null;
        } else if (questions[index].tipo_respuesta_label === 'lista') {
          // Pasaste a lista: limpia rangos y valida valor_objetivo contra la lista
          questions[index].semaforo.rango_min = '';
          questions[index].semaforo.rango_max = '';
          const opciones = (questions[index].lista || []).map(o => typeof o === 'string' ? o : o.nombre);
          if (!opciones.includes(questions[index].semaforo.valor_objetivo || '')) {
            questions[index].semaforo.valor_objetivo = '';
          }
        }
      }

      }

      if (name === 'aplica_semaforo' && !checked) {
        questions[index].maximo = '';
        questions[index].minimo = '';
        questions[index].respuesta_correcta_lista = '';
      }
      if (name === 'semaforo') {
          const questions = [...prev.preguntas];
          if (checked) {
            // si no hay objeto, crea uno base
            if (!questions[index].semaforo || typeof questions[index].semaforo !== 'object') {
              questions[index].semaforo = { id: undefined, rango_min: '', rango_max: '', valor_objetivo: null };
            }
          } else {
            questions[index].semaforo = null;
            // opcional: limpia campos auxiliares si usas otros
            questions[index].maximo = '';
            questions[index].minimo = '';
            questions[index].respuesta_correcta_lista = '';
          }
          return { ...prev, preguntas: questions };
        }


      return { ...prev, preguntas: questions };
    });
  };


  const handleAddQuestion = () => {
    setFormData(prev => ({
      ...prev,
      preguntas: [...prev.preguntas, createEmptyQuestion()]
    }));
  };

  const handleDeleteQuestion = async (index) => {
  const pregunta = formData.preguntas[index];

  // Si no tiene id (nueva sin guardar), solo la removemos del estado
  if (!pregunta?.id || (typeof pregunta.id === 'string' && pregunta.id.startsWith('new-'))) {
    setFormData(prev => {
      const preguntas = [...prev.preguntas];
      preguntas.splice(index, 1);
      return { ...prev, preguntas };
    });
    return;
  }

  // Optimistic UI: quita primero en UI
  const snapshot = JSON.parse(JSON.stringify(formData.preguntas));
  setFormData(prev => {
    const preguntas = [...prev.preguntas];
    preguntas.splice(index, 1);
    return { ...prev, preguntas };
  });

  try {
    await deletePreguntaCascade(pregunta.id);
    // opcional: refrescar desde backend para quedar 100% alineados
    // const resRutina = await api.get(`/tipos_rutinas/${id}`);
    // ...adaptar y setFormData/resincronizar
  } catch (err) {
    console.error('Error al borrar en cascada', err);
    alert('No se pudo borrar la pregunta. Revirtiendo cambios.');
    // rollback
    setFormData(prev => ({ ...prev, preguntas: snapshot }));
  }
};


  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleOptionInput = (index, e) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      e.preventDefault();
      const newOption = { nombre: e.target.value.trim(), is_active: true, id: null };
      const updatedQuestions = [...formData.preguntas];
      updatedQuestions[index].lista = [...(updatedQuestions[index].lista || []), newOption];
      setFormData({ ...formData, preguntas: updatedQuestions });
      e.target.value = '';
    }
  };

  const handleDeleteOpcion = (qIdx, opIdx) => {
  setFormData(prev => {
    const preguntas = [...prev.preguntas];
    const removed = preguntas[qIdx].lista[opIdx];
    const removedNombre = typeof removed === 'string' ? removed : removed?.nombre;
    preguntas[qIdx].lista.splice(opIdx, 1);

    if (preguntas[qIdx].semaforo?.valor_objetivo &&
        removedNombre === preguntas[qIdx].semaforo.valor_objetivo) {
      preguntas[qIdx].semaforo.valor_objetivo = '';
    }
    return { ...prev, preguntas };
  });
};


  const handleSave = async () => {
    try {
      await api.put(`/tipos_rutinas/${formData.id}`, {
        nombre: formData.nombre,
        fabricante_id: formData.fabricante_id,
        periodicidad: formData.periodicidad,
        ejecutor: formData.ejecutor,
        is_active: formData.is_active
      });

      for (const pregunta of formData.preguntas) {
  // --- 1) Semáforo ---
  let semaforoId = null;
  const compatible = isSemaforoCompatible(pregunta.tipo_respuesta_label);
  if (pregunta.semaforo&& compatible) {
    // Detectar tipo por etiqueta ya calculada
    const tipoLabel = pregunta.tipo_respuesta_label;
    if (tipoLabel === 'numero') {
      if (pregunta.semaforo?.id) {
        const res = await api.put(`/semaforos/${pregunta.semaforo.id}`, {
          // numero
          rango_max: pregunta.semaforo?.rango_max === '' ? null : Number(pregunta.semaforo.rango_max),
          rango_min: pregunta.semaforo?.rango_min === '' ? null : Number(pregunta.semaforo.rango_min),

          is_active: true,
        });
        semaforoId = res.data.id;
      } else {
        const res = await api.post('/semaforos/', {
          rango_max: Number(pregunta.semaforo.rango_max ?? pregunta.maximo ?? 0),
          rango_min: Number(pregunta.semaforo.rango_min ?? pregunta.minimo ?? 0),
          is_active: true,
        });
        semaforoId = res.data.id;
        // guarda el id en memoria para futuras ediciones
        pregunta.semaforo.id = res.data.id;
      }
    } else if (tipoLabel === 'lista') {
      if (pregunta.semaforo?.id) {
        const res = await api.put(`/semaforos/${pregunta.semaforo.id}`, {
          valor_objetivo: pregunta.semaforo.valor_objetivo ?? pregunta.respuesta_correcta_lista ?? '',
          is_active: true,
        });
        semaforoId = res.data.id;
      } else {
        const res = await api.post('/semaforos/', {
          valor_objetivo: pregunta.semaforo.valor_objetivo ?? pregunta.respuesta_correcta_lista ?? '',
          is_active: true,
        });
        semaforoId = res.data.id;
        pregunta.semaforo.id = res.data.id;
      }
    }
  }
  else {
    // Forzar null si quedó algo colgado
    pregunta.semaforo = null;
  }

  // --- 2) Upsert de la pregunta ---
  if (pregunta.id && !String(pregunta.id).startsWith('new-')) {
    await api.put(`/preguntas_rutinas/${pregunta.id}`, {
      tipo_rutina_id: formData.id,
      pregunta: pregunta.pregunta,
      id_tipo_respuesta: pregunta.tipo_respuesta, // <- ya viene del Select correcto
      orden: Number(pregunta.orden) || 0,
      semaforo_id: semaforoId,
      idioma: 'es',
      nombre_elemento: '',
      observaciones: '',
      responsabilidad: '',
    });
  } else {
    const res = await api.post('/preguntas_rutinas/', {
      tipo_rutina_id: formData.id,
      pregunta: pregunta.pregunta,
      id_tipo_respuesta: pregunta.tipo_respuesta,
      orden: Number(pregunta.orden) || 0,
      semaforo_id: semaforoId,
      idioma: 'es',
      nombre_elemento: '',
      observaciones: '',
      responsabilidad: '',
    });
    pregunta.id = res.data.id;
  }

  // --- 3) Sincroniza opciones de lista si aplica ---
  if (pregunta.tipo_respuesta_label === 'lista' && pregunta.id) {
    const res = await api.get(`/preguntas_rutinas/${pregunta.id}`);
    const opcionesBackend = res.data.lista || [];
    const opcionesFrontend = (pregunta.lista || []).map(o => (typeof o === 'string' ? { nombre: o } : o));

    // crear nuevas
    for (const op of opcionesFrontend) {
      const existe = opcionesBackend.some(ob => ob.nombre.trim() === (op.nombre || '').trim());
      if (!existe) {
        await api.post('/listas_respuestas/', {
          pregunta_id: pregunta.id,
          nombre: op.nombre,
          is_active: true,
        });
      }
    }
    // borrar removidas
    for (const op of opcionesBackend) {
      const sigue = opcionesFrontend.some(of => (of.nombre || '').trim() === op.nombre.trim());
      if (!sigue && op.id) {
        await api.delete(`/listas_respuestas/${op.id}`);
      }
    }
  }
}


      alert('Cambios guardados correctamente');
      setIsEditing(false);
      // Solo actualizamos originalFormData con los datos generales, ya que las preguntas no se modificaron
      setOriginalFormData(prev => ({
        ...prev,
        nombre: formData.nombre,
        fabricante_id: formData.fabricante_id,
        periodicidad: formData.periodicidad,
        ejecutor: formData.ejecutor,
        is_active: formData.is_active,
      }));
      if (onSave) onSave();
    } catch (error) {
      console.error("Error al guardar", error);
      alert("Error al guardar");
    }
  };


  const handleCancel = () => {
    // Restaurar solo los datos generales del original
    setFormData(prev => ({
      ...prev,
      nombre: originalFormData.nombre,
      fabricante_id: originalFormData.fabricante_id,
      periodicidad: originalFormData.periodicidad,
      ejecutor: originalFormData.ejecutor,
      is_active: originalFormData.is_active,
    }));
    setIsEditing(false);
  };

  return (
    <StyledMainContainer>
      <Header />
      {/* Título y navegación del detalle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, }}>
        <Typography variant="h5" component="h2">
          Detalle Tipo de Rutina
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Tipos de Rutinas &gt; Detalle tipo rutina
        </Typography>
      </Box>

      {/* Campos editables: Nombre, Fabricante, Tipo */}
      <Paper elevation={0} sx={{ p: 2, mb: 4, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 6 }}>
            <TextField
              fullWidth
              label="Nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleRoutineDetailChange}
              variant="outlined"
              size="small"
              disabled={!isEditing}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 6, sm: 6 }}>
            <TextField
              fullWidth
              label="Fabricante"
              name="fabricante"
              value={formData.fabricante?.nombre || ''}
              onChange={handleRoutineDetailChange}
              variant="outlined"
              size="small"
              disabled={true}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6 }}>
            <FormControl fullWidth variant="outlined" size="small" disabled={!isEditing}>
              <InputLabel>Periodicidad</InputLabel>
              <Select
                label="Periodicidad"
                name="periodicidad"
                value={formData.periodicidad}
                onChange={handleRoutineDetailChange}
              >
                {lista_periodicidad.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth variant="outlined" size="small" disabled={!isEditing} >
              <InputLabel>Ejecutor</InputLabel>
              <Select label="Ejecutor" name="ejecutor" value={formData.ejecutor} onChange={handleRoutineDetailChange}>
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                  name="is_active"
                  color="primary"
                  disabled={!isEditing}
                />
              }
              label="Estatus"
              labelPlacement="start"
              sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
            />
          </Grid>

        </Grid>
      </Paper>

      {/* Encabezado de la tabla y botón de añadir pregunta */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">Preguntas correspondientes al tipo rutina</Typography>
        {isEditing && (  //solo visible en modo edicion
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddQuestion}
            size="small"
            disabled={!isEditing}
          >
            Añadir Pregunta
          </Button>
        )}
      </Box>

      {/* Tabla de preguntas */}
      <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
        <Table sx={{ minWidth: 650 }} aria-label="preguntas de rutina">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '30%' }}>Pregunta</TableCell>
              <TableCell sx={{ width: '8%' }}>Orden</TableCell>
              <TableCell sx={{ width: '10%' }}>Semáforo</TableCell>
              <TableCell sx={{ width: '17%' }}>Rango válido</TableCell>
              <TableCell sx={{ width: '15%' }}>Tipo de respuesta</TableCell>
              <TableCell sx={{ width: '10%' }}>Opciones Lista</TableCell>
              {/*{isEditing && ( 
                <TableCell sx={{ width: '10%' }}>Acciones</TableCell>
              )}*/}
            </TableRow>
          </TableHead>
          <TableBody>
            {formData.preguntas && formData.preguntas.length > 0 ? (
              formData.preguntas.map((pregunta, index) => (
                
                <TableRow key={pregunta.id}>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={pregunta.pregunta}
                      onChange={(e) => handleQuestionChange(index, 'pregunta', e.target.value)}
                      disabled={!isEditing} //control de edicion
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={pregunta.orden}
                      onChange={(e) => handleQuestionChange(index, 'orden', parseInt(e.target.value) || '')}
                      disabled={!isEditing} //control de edicion
                    />
                  </TableCell>
                  <TableCell>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!pregunta.semaforo && (pregunta.tipo_respuesta_label === 'numero' || pregunta.tipo_respuesta_label === 'lista')}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData(prev => {
                              const preguntas = [...prev.preguntas];
                              const q = { ...preguntas[index] };

                              if (checked) {
                                if (q.tipo_respuesta_label === 'numero' || q.tipo_respuesta_label === 'lista') {
                                  q.semaforo = (q.semaforo && typeof q.semaforo === 'object')
                                    ? q.semaforo
                                    : { id: undefined, rango_min: '', rango_max: '', valor_objetivo: q.tipo_respuesta_label === 'lista' ? '' : null };
                                  // limpiar campos según tipo
                                  if (q.tipo_respuesta_label === 'numero') q.semaforo.valor_objetivo = null;
                                  if (q.tipo_respuesta_label === 'lista') { q.semaforo.rango_min = ''; q.semaforo.rango_max = ''; }
                                }
                              } else {
                                q.semaforo = null;
                              }

                              preguntas[index] = q;
                              return { ...prev, preguntas };
                            });
                          }}
                          disabled={!isEditing || !(pregunta.tipo_respuesta_label === 'numero' || pregunta.tipo_respuesta_label === 'lista')}
                        />



                      }
                      label="" // Etiqueta para el checkbox
                    />



                  </TableCell>
                  <TableCell>
                    {pregunta.semaforo && (pregunta.tipo_respuesta_label === 'numero' || pregunta.tipo_respuesta_label === 'lista') ? (
                      pregunta.tipo_respuesta_label === 'numero' ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            label="Min"
                            value={pregunta.semaforo?.rango_min ?? ''}   // usa ?? para permitir 0
                            onChange={(e) => {
                              const val = e.target.value; // mantener string mientras escribes
                              setFormData(prev => {
                                const preguntas = [...prev.preguntas];
                                preguntas[index] = {
                                  ...preguntas[index],
                                  semaforo: {
                                    ...(preguntas[index].semaforo || { valor_objetivo: null }),
                                    rango_min: val,
                                    rango_max: preguntas[index].semaforo?.rango_max ?? '',
                                    valor_objetivo: null, // asegúrate de limpiar si venías de 'lista'
                                  }
                                };
                                return { ...prev, preguntas };
                              });
                            }}
                            disabled={!isEditing}
                          />
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            label="Max"
                            value={pregunta.semaforo?.rango_max ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormData(prev => {
                                const preguntas = [...prev.preguntas];
                                preguntas[index] = {
                                  ...preguntas[index],
                                  semaforo: {
                                    ...(preguntas[index].semaforo || { valor_objetivo: null }),
                                    rango_min: preguntas[index].semaforo?.rango_min ?? '',
                                    rango_max: val,
                                    valor_objetivo: null,
                                  }
                                };
                                return { ...prev, preguntas };
                              });
                            }}
                            disabled={!isEditing}
                          />
                        </Box>
                      ) : (
                        // tipo_respuesta_label === 'lista'
                        <FormControl fullWidth size="small" disabled={!isEditing || !(pregunta.lista?.length)}>
                          <InputLabel>Valor objetivo</InputLabel>
                          <Select
                            label="Valor objetivo"
                            value={pregunta.semaforo?.valor_objetivo ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormData(prev => {
                                const preguntas = [...prev.preguntas];
                                preguntas[index] = {
                                  ...preguntas[index],
                                  semaforo: {
                                    ...(preguntas[index].semaforo || {}),
                                    valor_objetivo: val,
                                    // limpia rangos si venías de 'numero'
                                    rango_min: '',
                                    rango_max: '',
                                  }
                                };
                                return { ...prev, preguntas };
                              });
                            }}
                            displayEmpty
                          >
                            <MenuItem value=""><em></em></MenuItem>
                            {(pregunta.lista || []).map((op) => {
                              const nombre = typeof op === 'string' ? op : op.nombre;
                              const key = (op && op.id) || nombre;
                              return (
                                <MenuItem key={key} value={nombre}>{nombre}</MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        N/A
                      </Typography>
                    )}
                  </TableCell>



                  <TableCell>
                    <FormControl fullWidth size="small" disabled={!isEditing}>
                      <Select
                        value={pregunta.tipo_respuesta || ''}
                        onChange={(e) => handleQuestionChange(index, 'tipo_respuesta', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Selecciona un tipo</MenuItem>
                        {tiposRespuestas.map(tr => (
                          <MenuItem key={tr.id} value={tr.id}>{tr.tipo}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                  </TableCell>

                  <TableCell>
                    {pregunta.tipo_respuesta_label === 'lista' && (
                      <Box
                        sx={{
                          border: '1px solid #ccc',
                          borderRadius: 2,
                          p: 1,
                          minHeight: 65,
                          maxHeight: 100,
                          maxWidth: 300,
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            overflowX: 'auto',
                            gap: 1,
                          }}
                        >
                          {(pregunta.lista || []).map((item, i) => (
                            <Box
                              key={item.id || i}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: 'grey.200',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '16px',
                                fontSize: 13,
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                              }}
                            >
                              {item.nombre}
                              
                            {isEditing && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteOpcion(index, i)}
                                sx={{ ml: 1 }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                            
                            </Box>
                          ))}
                        </Box>

                        
                      {isEditing && (
                        <TextField
                          size="small"
                          placeholder="Añadir opción y presiona Enter"
                          onKeyDown={(e) => handleOptionInput(index, e)}
                          variant="outlined"
                        />
                      )}
                         
                      </Box>
                    )}


                  </TableCell>
                  
                  {isEditing && ( //boton eliminar solo en modo edicion
                    <TableCell>
                      <IconButton onClick={() => handleDeleteQuestion(index)} aria-label="eliminar pregunta" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                    
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={isEditing ? 7 : 6} sx={{ textAlign: 'center', py: 3 }}>
                  No se encontraron preguntas para esta rutina.
                  {!isEditing && " Presiona 'Editar' para añadir preguntas."}
                  {isEditing && " Haz clic en 'Añadir Pregunta' para empezar."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Botones de acción (Editar, Guardar, Cancelar) */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {!isEditing && ( //* Botón Editar solo si NO está editando */}
          <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={handleEdit} disabled={false}>
            Editar
          </Button>
        )}
        {isEditing && ( //* Botones Guardar y Cancelar solo si está editando */}
          <>
            <Button variant="outlined" color="secondary" onClick={handleCancel}
              sx={{
                color: (theme) => theme.palette.primary.main,
                borderColor: (theme) => theme.palette.primary.main,
              }}>
              Cancelar
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Guardar Cambios
            </Button>
          </>
        )}
      </Box>
    </StyledMainContainer>
  );
}

export default TipoRutinaDetail;